/**
 * IncidentReportForm — Safety & Risk Management report filed by any
 * NECS employee with a company vehicle. Supports:
 *   • Incident type selection (collision · minor accident · vandalism ·
 *     theft · roadside towing/battery/lockout/flat tire/other)
 *   • Police involvement + case number
 *   • Insurance carrier / policy / claim number + open-claim flag
 *   • Roadside assistance request flag
 *   • Photo gallery upload (data-url form; canonical NECS shape)
 *   • Location + State + Region + City for color-coded triage in the
 *     Safety & Risk Management Command Center
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  AlertTriangle, Camera, Shield, Phone, Car, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const INCIDENT_TYPES = [
  { key: 'collision', label: 'Collision', icon: Car },
  { key: 'minor_accident', label: 'Minor Accident', icon: Car },
  { key: 'roadside_towing', label: 'Roadside · Towing', icon: Phone },
  { key: 'roadside_battery', label: 'Roadside · Dead Battery', icon: Phone },
  { key: 'roadside_lockout', label: 'Roadside · Lockout', icon: Phone },
  { key: 'roadside_flat_tire', label: 'Roadside · Flat Tire', icon: Phone },
  { key: 'roadside_other', label: 'Roadside · Other', icon: Phone },
  { key: 'vandalism', label: 'Vandalism', icon: Shield },
  { key: 'theft', label: 'Theft', icon: Shield },
  { key: 'other', label: 'Other', icon: AlertTriangle },
];

export default function IncidentReportForm({ regions = [], vehicles = [], onSaved }) {
  const [form, setForm] = useState({
    vehicle_id: '', incident_type: '', description: '',
    location: '', state: '', region_id: '', city: '',
    police_involved: false, police_case_number: '',
    insurance_carrier: '', insurance_policy: '', claim_number: '',
    needs_claim: false, roadside_requested: false,
    photos: [],
  });
  const [saving, setSaving] = useState(false);

  const isRoadside = form.incident_type.startsWith('roadside_');

  const filteredRegions = regions
    .filter((r) => !form.state || (r.state || 'TX').toUpperCase() === form.state)
    .sort((a, b) => (a.region_code || '').localeCompare(b.region_code || ''));

  const readPhotos = async (files) => {
    const arr = await Promise.all(Array.from(files).slice(0, 8).map((f) => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res({ data: reader.result });
      reader.readAsDataURL(f);
    })));
    setForm((f) => ({ ...f, photos: [...(f.photos || []), ...arr].slice(0, 12) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.incident_type) return toast.error('Pick an incident type');
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/safety-incidents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          roadside_requested: form.roadside_requested || isRoadside,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Incident report filed — Safety & Risk Management notified');
      setForm({
        vehicle_id: '', incident_type: '', description: '',
        location: '', state: '', region_id: '', city: '',
        police_involved: false, police_case_number: '',
        insurance_carrier: '', insurance_policy: '', claim_number: '',
        needs_claim: false, roadside_requested: false, photos: [],
      });
      if (onSaved) onSaved(await res.json());
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900 border-2 border-red-500/50 p-4" data-testid="incident-report-form">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-red-500" />
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          FILE INCIDENT / ROADSIDE REPORT
        </h3>
      </div>
      <form onSubmit={submit} className="space-y-3">
        {/* Incident type pills */}
        <div>
          <Label className="text-xs text-zinc-400">Incident type *</Label>
          <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="incident-type-pills">
            {INCIDENT_TYPES.map((t) => {
              const Icon = t.icon;
              const active = form.incident_type === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, incident_type: t.key }))}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold border ${
                    active
                      ? 'bg-red-600 border-red-500 text-white'
                      : 'bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-900'
                  }`}
                  data-testid={`incident-type-${t.key}`}
                >
                  <Icon className="h-3 w-3" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vehicle + Location grid */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Vehicle</Label>
            <Select value={form.vehicle_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_id: v }))}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="incident-vehicle">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                {vehicles.map((v) => (
                  <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                    {v.make_model || v.license_plate || v.vehicle_id}
                  </SelectItem>
                ))}
                {vehicles.length === 0 && <SelectItem value="__none__" disabled>No vehicles yet</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>State</Label>
            <Select value={form.state || undefined} onValueChange={(v) => setForm((f) => ({ ...f, state: v, region_id: '' }))}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="incident-state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Region</Label>
            <Select value={form.region_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, region_id: v }))}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="incident-region">
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                {filteredRegions.map((r) => (
                  <SelectItem key={r.region_id} value={r.region_id}>{r.region_code} · {r.name}</SelectItem>
                ))}
                {filteredRegions.length === 0 && <SelectItem value="__none__" disabled>Pick a state first</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} placeholder="e.g. Dallas" className="bg-zinc-800 border-zinc-700" data-testid="incident-city" />
          </div>
          <div className="col-span-2">
            <Label>Location details</Label>
            <Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Cross streets, address, mile marker…" className="bg-zinc-800 border-zinc-700" data-testid="incident-location" />
          </div>
        </div>

        {/* Description */}
        <div>
          <Label>Description</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="What happened, injuries, damages, witnesses…" className="bg-zinc-800 border-zinc-700" data-testid="incident-description" />
        </div>

        {/* Police + Insurance */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-zinc-700 rounded p-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px]">Police involved</Label>
              <Switch checked={form.police_involved} onCheckedChange={(v) => setForm((f) => ({ ...f, police_involved: v }))} data-testid="incident-police-toggle" />
            </div>
            <Input placeholder="Police case #" value={form.police_case_number} onChange={(e) => setForm((f) => ({ ...f, police_case_number: e.target.value }))} disabled={!form.police_involved} className="bg-zinc-950 border-zinc-700 h-8 text-[12px]" data-testid="incident-police-case" />
          </div>
          <div className="border border-zinc-700 rounded p-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px]">Open insurance claim</Label>
              <Switch checked={form.needs_claim} onCheckedChange={(v) => setForm((f) => ({ ...f, needs_claim: v }))} data-testid="incident-claim-toggle" />
            </div>
            <Input placeholder="Carrier (e.g. Progressive)" value={form.insurance_carrier} onChange={(e) => setForm((f) => ({ ...f, insurance_carrier: e.target.value }))} className="bg-zinc-950 border-zinc-700 h-8 text-[12px]" data-testid="incident-insurance-carrier" />
            <div className="grid grid-cols-2 gap-1.5">
              <Input placeholder="Policy #" value={form.insurance_policy} onChange={(e) => setForm((f) => ({ ...f, insurance_policy: e.target.value }))} className="bg-zinc-950 border-zinc-700 h-8 text-[12px]" data-testid="incident-insurance-policy" />
              <Input placeholder="Claim #" value={form.claim_number} onChange={(e) => setForm((f) => ({ ...f, claim_number: e.target.value }))} className="bg-zinc-950 border-zinc-700 h-8 text-[12px]" data-testid="incident-claim-number" />
            </div>
          </div>
        </div>

        {/* Roadside toggle */}
        <div className="flex items-center justify-between border border-amber-500/40 bg-amber-500/5 rounded p-2">
          <div>
            <Label className="text-amber-300 text-[12px]">Roadside assistance requested</Label>
            <p className="text-[10px] text-zinc-500">Ops will dispatch a tow truck, jump start, lockout, or tire service.</p>
          </div>
          <Switch checked={form.roadside_requested || isRoadside} onCheckedChange={(v) => setForm((f) => ({ ...f, roadside_requested: v }))} disabled={isRoadside} data-testid="incident-roadside-toggle" />
        </div>

        {/* Photo gallery */}
        <div>
          <Label className="flex items-center gap-1.5"><Camera className="h-3 w-3" /> Photos (up to 12)</Label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => e.target.files && readPhotos(e.target.files)}
            className="text-[11px] mt-1"
            data-testid="incident-photo-input"
          />
          {form.photos.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="incident-photo-preview">
              {form.photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.data} alt="" className="h-16 w-16 object-cover rounded border border-zinc-700" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                          className="absolute -top-1 -right-1 bg-red-600 rounded-full h-4 w-4 flex items-center justify-center" data-testid={`incident-photo-remove-${i}`}>
                    <X className="h-2.5 w-2.5 text-white" />
                  </button>
                </div>
              ))}
              <Badge className="bg-zinc-700 text-white text-[10px]">{form.photos.length}/12</Badge>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-zinc-800">
          <Button type="submit" disabled={saving || !form.incident_type}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold w-full"
                  data-testid="incident-submit">
            {saving ? 'Filing…' : 'File Report → Safety & Risk Management'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
